import Link from 'next/link'
import Router from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageSelector } from 'web/components/language-selector'
import { useTrendingTopics } from 'web/components/search/query-topics'
import { useAPIGetter } from 'web/hooks/use-api-getter'
import { useUser } from 'web/hooks/use-user'
import { Contract } from 'common/contract'
import { Group } from 'common/group'
import { SEARCH_TOPICS_TO_SUBTOPICS } from 'common/topics'
import { formatMoney } from 'common/util/format'
import { getDisplayProbability } from 'common/calculate'
import { CPMMContract } from 'common/contract'
import { Tooltip } from 'web/components/widgets/tooltip'
import { firebaseLogin, firebaseLogout } from 'web/lib/firebase/users'
import { Avatar } from 'web/components/widgets/avatar'

// Categorias principais do menu
const MAIN_CATEGORIES = [
  { tKey: 'nav.trending', icon: true, sort: 'score' },
  { tKey: 'nav.breaking', sort: 'last-updated' },
  { tKey: 'nav.new', sort: 'newest' },
]

// Categorias secundárias baseadas nos topics reais
const CATEGORY_LINKS = Object.keys(SEARCH_TOPICS_TO_SUBTOPICS).map((key) => ({
  name: key.replace(/^[^\s]+\s/, ''), // Remove emoji
  key: key,
}))

// Formata volume para display
function formatVolume(volume: number): string {
  if (volume >= 1000000) {
    return `$${(volume / 1000000).toFixed(1)}m`
  } else if (volume >= 1000) {
    return `$${(volume / 1000).toFixed(0)}k`
  }
  return `$${volume.toFixed(0)}`
}

// Formata número de traders
function formatTraders(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`
  }
  return count.toString()
}

// Pega probabilidade formatada com segurança
function getProbability(contract: Contract): number {
  try {
    if ('prob' in contract && 'pool' in contract && 'p' in contract) {
      const prob = getDisplayProbability(contract as CPMMContract)
      return Math.round(prob * 100)
    }
    return 50
  } catch {
    return 50
  }
}

// Pega as opções do contrato (para multi-choice)
function getContractOptions(contract: Contract): { name: string; probability: number }[] {
  try {
    if ('answers' in contract && contract.answers && contract.answers.length > 0) {
      return contract.answers.slice(0, 2).map((answer) => ({
        name: answer.text.length > 20 ? answer.text.substring(0, 20) + '...' : answer.text,
        probability: Math.round((answer.prob ?? 0) * 100),
      }))
    }
    // Binary contract
    const prob = getProbability(contract)
    return [{ name: 'Yes', probability: prob }]
  } catch {
    return [{ name: 'Yes', probability: 50 }]
  }
}

export default function NewHome() {
  const { t } = useTranslation()
  const user = useUser()
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('score')
  const [searchQuery, setSearchQuery] = useState('')

  // Busca trending topics reais
  const trendingTopics = useTrendingTopics(15) as Group[] | undefined

  // Busca mercados reais
  const { data: markets, loading: marketsLoading } = useAPIGetter('search-markets-full', {
    term: '',
    sort: selectedCategory as any,
    filter: 'open',
    limit: 20,
    gids: selectedTopic ?? undefined,
  })

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      {/* ========== HEADER ========== */}
      <header className="sticky top-0 z-50 bg-white" style={{ borderBottom: '1px solid #e8e8e8' }}>
        <div className="mx-auto flex items-center justify-between px-6 py-3" style={{ maxWidth: '1400px' }}>
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="4" fill="#000" />
              <path d="M7 20L14 7L21 20H17L14 13L11 20H7Z" fill="white" />
            </svg>
            <span style={{ fontSize: '22px', fontWeight: 600, color: '#000', letterSpacing: '-0.5px' }}>
              {t('brand.name')}
            </span>
          </Link>

          {/* Search */}
          <div className="flex-1 mx-8 max-w-xl">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#858585" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={t('nav.searchPlaceholder')}
                className="w-full h-[42px] pl-11 pr-10 outline-none text-[15px] text-black placeholder-[#858585]"
                style={{ backgroundColor: '#F4F5F6', borderRadius: '4px', border: 'none' }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    Router.push(`/browse?q=${encodeURIComponent(searchQuery.trim())}`)
                  }
                }}
              />
              <div 
                className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[12px] text-[#999] bg-white"
                style={{ borderRadius: '4px', border: '1px solid #d0d0d0' }}
              >
                /
              </div>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1">
            <LanguageSelector />
            <Link href="/about" className="flex items-center gap-2 px-3 py-2 text-[15px] font-medium text-black">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="8" fill="#2563EB" />
                <text x="8" y="12" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">i</text>
              </svg>
              <span>{t('nav.howItWorks')}</span>
            </Link>

            {user ? (
              <>
                <Link href="/create" className="px-4 py-2 text-[15px] font-semibold text-[#2563EB]">
                  {t('nav.create')}
                </Link>
                <Link href={`/${user.username}`}>
                  <Avatar avatarUrl={user.avatarUrl} username={user.username} size="sm" />
                </Link>
              </>
            ) : (
              <>
                <Link 
                  href="/login"
                  className="px-4 py-2 text-[15px] font-semibold text-[#2563EB]"
                >
                  {t('nav.login')}
                </Link>

                <Link 
                  href="/signup"
                  className="px-4 py-2.5 text-[15px] font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8]"
                  style={{ borderRadius: '8px' }}
                >
                  {t('nav.signup')}
                </Link>
              </>
            )}

            <button className="ml-2 p-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* ========== CATEGORY NAV ========== */}
      <nav className="bg-white" style={{ borderBottom: '1px solid #e8e8e8' }}>
        <div className="mx-auto flex items-center px-6 overflow-x-auto" style={{ maxWidth: '1400px' }}>
          {/* Trending, Breaking, New */}
          {MAIN_CATEGORIES.map((cat) => (
            <button 
              key={cat.tKey}
              onClick={() => setSelectedCategory(cat.sort)}
              className={`flex items-center gap-1 px-3 py-3 text-[14px] font-medium transition-colors ${
                selectedCategory === cat.sort ? 'text-black' : 'text-[#666] hover:text-black'
              }`}
            >
              {cat.icon && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              )}
              {t(cat.tKey)}
            </button>
          ))}

          {/* Separator */}
          <div className="mx-2 h-5 w-px bg-[#e5e5e5]" />

          {/* Category links */}
          {CATEGORY_LINKS.map((cat) => (
            <Link 
              key={cat.key}
              href={`/browse?topic=${encodeURIComponent(cat.key)}`}
              className="px-3 py-3 text-[14px] font-medium text-[#666] hover:text-black whitespace-nowrap"
            >
              {cat.name}
            </Link>
          ))}

          {/* More */}
          <button className="flex items-center gap-1 px-3 py-3 text-[14px] font-medium text-[#666] hover:text-black">
            {t('common.more')}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </nav>

      {/* ========== FILTERS BAR ========== */}
      <div className="bg-white" style={{ borderBottom: '1px solid #e8e8e8' }}>
        <div className="mx-auto flex items-center gap-4 px-6 py-3" style={{ maxWidth: '1400px' }}>
          {/* Search input */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder={t('common.search')}
              className="h-9 w-40 pl-9 pr-3 outline-none text-[14px] text-black placeholder-[#999]"
              style={{ backgroundColor: '#F4F5F6', border: 'none', borderRadius: '4px' }}
            />
          </div>

          {/* Filter icon */}
          <button className="p-2 text-[#666] hover:text-black">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="16" y2="12" />
              <line x1="4" y1="18" x2="12" y2="18" />
            </svg>
          </button>

          {/* Bookmark icon */}
          <button className="p-2 text-[#666] hover:text-black">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
          </button>

          {/* Topic pills - dados reais */}
          <div className="flex items-center gap-2 overflow-x-auto flex-1">
            <button
              onClick={() => setSelectedTopic(null)}
              className={`shrink-0 px-3 py-1.5 text-[14px] font-medium transition-colors ${
                selectedTopic === null ? 'text-[#2563EB]' : 'text-[#666] hover:text-black'
              }`}
              style={{ 
                borderRadius: '8px',
                backgroundColor: selectedTopic === null ? '#EEF2FF' : 'transparent'
              }}
            >
              {t('common.all')}
            </button>
            {trendingTopics?.slice(0, 12).map((topic) => (
              <button
                key={topic.id}
                onClick={() => setSelectedTopic(topic.id)}
                className={`shrink-0 px-3 py-1.5 text-[14px] font-medium transition-colors ${
                  selectedTopic === topic.id ? 'text-[#2563EB]' : 'text-[#666] hover:text-black'
                }`}
                style={{ 
                  borderRadius: '8px',
                  backgroundColor: selectedTopic === topic.id ? '#EEF2FF' : 'transparent'
                }}
              >
                {topic.name}
              </button>
            ))}
          </div>

          {/* Arrows */}
          <div className="ml-auto flex items-center gap-1">
            <button className="p-1 text-[#999] hover:text-black">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button className="p-1 text-[#999] hover:text-black">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ========== MARKETS GRID ========== */}
      <main className="mx-auto px-6 py-4" style={{ maxWidth: '1400px' }}>
        {marketsLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i}
                className="bg-white p-4 animate-pulse"
                style={{ borderRadius: '12px', border: '1px solid #e8e8e8' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-lg bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-8 bg-gray-100 rounded" />
                  <div className="h-8 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {markets?.map((market) => (
              <Link
                href={`/event/${market.slug}`}
                key={market.id}
                className="bg-white p-4 hover:shadow-md transition-shadow cursor-pointer block"
                style={{ borderRadius: '12px', border: '1px solid #e8e8e8' }}
              >
                {/* Header with image and title */}
                <div className="flex items-start gap-3 mb-3">
                  <img 
                    src={market.coverImageUrl || market.creatorAvatarUrl || '/logo.svg'} 
                    alt="" 
                    className="w-12 h-12 rounded-lg object-cover"
                    style={{ minWidth: '48px' }}
                  />
                  <h3 className="text-[15px] font-semibold text-black leading-snug line-clamp-2">
                    {market.question}
                  </h3>
                </div>

                {/* Options */}
                <div className="space-y-2 mb-3">
                  {getContractOptions(market).map((option, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-[14px] text-[#666] truncate max-w-[120px]">{option.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-semibold text-black">{option.probability}%</span>
                        <button 
                          className="px-3 py-1 text-[13px] font-medium text-[#16A34A] hover:bg-[#DCFCE7]"
                          style={{ borderRadius: '6px', border: '1px solid #BBF7D0' }}
                          onClick={(e) => {
                            e.preventDefault()
                            Router.push(`/event/${market.slug}?bet=yes`)
                          }}
                        >
                          {t('common.yes')}
                        </button>
                        <button 
                          className="px-3 py-1 text-[13px] font-medium text-[#DC2626] hover:bg-[#FEE2E2]"
                          style={{ borderRadius: '6px', border: '1px solid #FECACA' }}
                          onClick={(e) => {
                            e.preventDefault()
                            Router.push(`/event/${market.slug}?bet=no`)
                          }}
                        >
                          {t('common.no')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-3 text-[12px] text-[#999]">
                    {/* Unique Traders */}
                    <Tooltip text={t('marketCard.tradersTooltip', { count: market.uniqueBettorCount ?? 0 })}>
                      <div className="flex items-center gap-1 cursor-help">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                        </svg>
                        <span>{formatTraders(market.uniqueBettorCount ?? 0)}</span>
                      </div>
                    </Tooltip>
                    {/* Liquidity */}
                    <Tooltip text={t('marketCard.liquidityTooltip', { amount: ((market as any).totalLiquidity ?? 0).toLocaleString() })}>
                      <div className="flex items-center gap-1 cursor-help">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                        </svg>
                      </div>
                    </Tooltip>
                    {/* Volume */}
                    <Tooltip text={t('marketCard.volumeTooltip', { amount: (market.volume ?? 0).toLocaleString() })}>
                      <span className="cursor-help">
                        {formatVolume(market.volume)}
                      </span>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-1 text-[#999] hover:text-black" onClick={(e) => e.preventDefault()}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                    </button>
                    <button className="p-1 text-[#999] hover:text-black" onClick={(e) => e.preventDefault()}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* ========== FOOTER FIXO ========== */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 bg-white">
        <div className="mx-auto flex items-center justify-between px-6 py-3" style={{ maxWidth: '1400px' }}>
          <div className="flex items-center gap-1 text-[12px] text-[#666]">
            <span>{t('common.copyright')}</span>
            <span className="mx-2">·</span>
            <Link href="/privacy" className="hover:text-black">{t('footer.privacy')}</Link>
            <span className="mx-2">·</span>
            <Link href="/terms" className="hover:text-black">{t('footer.terms')}</Link>
            <span className="mx-2">·</span>
            <Link href="/about" className="hover:text-black">{t('footer.learn')}</Link>
            <span className="mx-2">·</span>
            <Link href="/careers" className="hover:text-black">{t('footer.careers')}</Link>
            <span className="mx-2">·</span>
            <Link href="/press" className="hover:text-black">{t('footer.press')}</Link>
          </div>
          <div className="flex items-center gap-4">
            <a href="mailto:contact@predita.com" className="text-[#666] hover:text-black">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-black">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-black">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-black">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-black">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            </a>
          </div>
        </div>
      </footer>

      {/* Spacer para o footer não cobrir conteúdo */}
      <div className="h-16" />
    </div>
  )
}

